import { LearningObjectService } from "@/services/learningObject.service";
import { offlineStorage } from "@/services/offline/offlineStorage";
import { fetchTranscript } from "@/services/offline/transcriptService";

export default {
  namespaced: true,
  state: {
    learningObjects: [],
    drm: null,
    ratings: {
      value: 0,
      count: 0,
      updated: true,
    },
    aiContent: null,
    aiContentLoId: null,
  },
  getters: {
    //
  },
  actions: {
    // `persistOnly` = pré-busca do download: só grava o ai.json e não encosta no
    // store nem fala com o usuário.
    //
    // Sem isso, baixar um módulo enquanto o aluno assiste a uma aula despachava
    // getAI para cada aula concluída, e como o SET_AI é um slot único a aba de IA
    // passava a mostrar o resumo de OUTRA aula — sem se corrigir sozinha, porque
    // abrir a aba não re-busca nada. O prefetch nunca precisou do store: ele quer
    // o arquivo em disco.
    /**
     * Story-2499: transcricao (WebVTT) de uma aula de audio.
     *
     * Devolve null sem rede ou em qualquer falha: a transcricao e um extra da aula, e o
     * audio continua tocando sem ela. Offline nao ha fallback em disco por enquanto — o
     * prefetch de transcricao ainda so cobre video (download.js).
     */
    getSubtitle(_, learningObjectId) {
      if (!this.state.dom.isConnected) return Promise.resolve(null);

      return LearningObjectService.getSubtitle(learningObjectId).catch(() => null);
    },

    getAI({ commit }, payload) {
      const loId = payload.LearningObjectId;
      const persistOnly = payload.persistOnly === true;

      if (!this.state.dom.isConnected) {
        // Sem rede não há o que persistir; o prefetch não tem o que fazer aqui.
        if (persistOnly) return Promise.resolve();
        return offlineStorage.getAI(loId)
          .then((cached) => commit('SET_AI', { loId, content: cached || null }))
          .catch(() => commit('SET_AI', { loId, content: null }));
      }

      return LearningObjectService.getAI(loId)
        .then((response) => {
          const responseData = response.data;
          let parsedAiQuiz;

          try {
            if (responseData.quiz) {
              const aiQuiz = JSON.parse(responseData.quiz);
              parsedAiQuiz = {
                questions: aiQuiz.perguntas.map((question) => ({
                  question: question.pergunta,
                  alternatives: question.alternativas,
                  answer: question.resposta_correta,
                })),
              };
            }
          } catch (error) {
            console.error("[AIQuiz-Parser]", error);
          }

          const aiContent = { ...responseData, quiz: parsedAiQuiz };
          if (!persistOnly) commit("SET_AI", { loId, content: aiContent });
          // Write-through pro cache offline (fire-and-forget). No app grava
          // offline/<loId>/ai.json; na web cai no localStorage.
          offlineStorage.saveAI(loId, aiContent);
        })
        .catch((error) => {
          // Pré-busca é trabalho de fundo: o aluno pediu para baixar a aula, não
          // para buscar IA. Um toast por aula num lote de 11 — todos com a
          // mensagem genérica de "entre em contato com o suporte", que é o que o
          // _getStatusMessage devolve no default — assusta sem informar nada
          // acionável. Fica no console.
          if (persistOnly) {
            console.warn('[getAI] pré-busca falhou para o LO', loId, error && error.message);
            return;
          }
          commit(
            "toast/NEW",
            { type: "error", message: error.message, suppressOffline: true },
            { root: true }
          );
        });
    },

    // Pré-busca a transcrição no download e cacheia em offline/<loId>/transcript.json.
    // Fire-and-forget — não comita no state. Quem injeta é o
    // `loadOfflineTranscript` do StudentClass, no coremediaReady, lendo do disco
    // e comitando no mesmo módulo que o caminho online usa.
    async prefetchTranscript(_ctx, { loId, broadcastId, token, customerId }) {
      const data = await fetchTranscript(broadcastId, token, customerId);
      if (data) await offlineStorage.saveTranscript(loId, data);
    },

    getRatings({ commit }, payload) {
      return LearningObjectService.getRatings(payload.LearningObjectId)
        .then((response) => {
          commit("SET_LEARNING_OBJECT_RATINGS", response.data);
          commit("SET_RATINGS_UPDATED", true);
        })
        .catch((error) => {
          commit(
            "toast/NEW",
            { type: "error", message: error.message, suppressOffline: true },
            { root: true }
          );
        });
    },
    getDRM({ commit, dispatch }, courseId) {
      return LearningObjectService.getDMRConfig(courseId)
        .then((response) => {
          commit("SET_DRM", response.data);
          dispatch("download/applyDrmEnforcement", { courseId }, { root: true });
        })
        .catch((error) => {
          commit(
            "toast/NEW",
            { type: "error", message: error.message, suppressOffline: true },
            { root: true }
          );
        });
    },


    getFileLearningObjectAuth({ commit }, payload) {
      return LearningObjectService.getFileLearningObjectAuth(payload.courseId, payload.learningObjectId)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          commit(
            "toast/NEW",
            { type: "error", message: error.message, suppressOffline: true },
            { root: true }
          );
        });
    },

    getDMRAuth({ commit }, payload) {
      return LearningObjectService.getDMRAuth(payload.courseId, payload.fileId)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          commit(
            "toast/NEW",
            { type: "error", message: error.message, suppressOffline: true },
            { root: true }
          );
        });
    },

    // Otimista: a nota entra no store antes da resposta — botão/estrelas mudam
    // na hora — e só volta atrás se o servidor recusar. `updated` segue como
    // trava de request em voo. A escolha insert/update tem de sair do valor
    // ANTERIOR ao commit otimista, senão toda avaliação nova viraria update.
    setRatings({ state, commit }, payload) {
      if (!state.ratings.updated) return;

      const prev = { rating: state.ratings.value, total: state.ratings.count };
      const isFirstRating = prev.rating === 0;

      commit("SET_RATINGS_UPDATED", false);
      commit("SET_LEARNING_OBJECT_RATINGS", {
        rating: payload.Rating,
        total: isFirstRating ? prev.total + 1 : prev.total,
      });

      const request = isFirstRating
        ? LearningObjectService.insertRatings(payload.LearningObjectId, payload.Rating)
        : LearningObjectService.updateRatings(payload.LearningObjectId, payload.Rating);

      return request
        .catch((error) => {
          commit("SET_LEARNING_OBJECT_RATINGS", prev);
          commit(
            "toast/NEW",
            { type: "error", message: error.message, suppressOffline: true },
            { root: true }
          );
        })
        .finally(() => {
          commit("SET_RATINGS_UPDATED", true);
        });
    },
  },
  mutations: {
    SET_LEARNING_OBJECT_RATINGS(state, data) {
      state.ratings.value = data.rating;
      state.ratings.count = data.total;
    },
    SET_RATINGS_UPDATED(state, updated) {
      state.ratings.updated = updated;
    },
    // Carimba de quem é o conteúdo. O `load()` não aguarda o getAI, então trocar
    // de aula rápido deixava a resposta da aula anterior chegar por último e
    // vencer. Quem exibe compara com a aula corrente antes de renderizar.
    SET_AI(state, { loId, content }) {
      state.aiContent = content;
      state.aiContentLoId = loId == null ? null : String(loId);
    },
    SET_DRM(state, drm) {
      state.drm = drm;
    },
  },
};
